/**
 * THE NIGHT'S SEED.
 *
 * The map is fixed on purpose — learnable routes and guard mastery are the whole
 * point — but a fixed map with fixed contents is a map you solve once. The second
 * run is the first run from memory: the same ring in the same case, the same
 * guard at the same corner at the same second. So everything that CAN vary
 * without making the building unlearnable varies, and all of it hangs off one
 * unsigned integer.
 *
 * That integer has to be the same on every machine in the round, which is the
 * reason this is a seeded generator and not `Math.random`. The host mints a seed,
 * ships it with the round reset, and every client deals the same containers and
 * starts the same guards on the same corners. A generator that could not do that
 * would mean a thief on one screen looting a case that is empty on another.
 *
 * mulberry32: thirty-two bits of state, four operations, a period of 2^32 and a
 * distribution far better than anything a round of a heist game can distinguish.
 * The point is not statistical quality, it is that it is short enough to be
 * obviously correct and identical everywhere.
 */

export class Rng {
  private state: number;

  constructor(seed: number) {
    // Zero is a legitimate seed to be handed and a terrible one to run from, so
    // it is folded away rather than rejected.
    this.state = (seed >>> 0) || 0x9e3779b9;
  }

  /** The next float in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** An integer in [0, n). Returns 0 for n <= 0 rather than NaN. */
  int(n: number): number {
    return n > 0 ? Math.floor(this.next() * n) | 0 : 0;
  }

  /** One of them, or undefined if there are none. */
  pick<T>(items: readonly T[]): T | undefined {
    return items.length ? items[this.int(items.length)] : undefined;
  }

  /**
   * Fisher-Yates, in place, on a copy.
   *
   * A copy because every caller so far wants the original order left alone —
   * shuffling a store's spawn table in place would permanently scramble the
   * catalogue for the rest of the process, and the bug would look like a data
   * problem rather than an aliasing one.
   */
  shuffle<T>(items: readonly T[]): T[] {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  }
}

/**
 * A fresh seed for a new night.
 *
 * `Math.random` is fine HERE and nowhere else downstream of it: this runs once,
 * on the host, to choose a number that is then shared. Everything that consumes
 * it must be reproducible from it.
 *
 * Deliberately drawn from the NAMEABLE range rather than the full thirty-two
 * bits. A name a player can read off the briefing card is worthless if typing it
 * back gives them a different building, and it would — four letters address
 * about seven hundred thousand values, not four billion. So the seed space is
 * the name space, the card is a complete handle on the night, and seven hundred
 * thousand distinct malls is several lifetimes of them.
 */
export function mintSeed(): number {
  return Math.floor(Math.random() * NAMEABLE);
}

/**
 * The seed as a player can read it back to you.
 *
 * Four letters from an alphabet with no vowels and no lookalikes, so a crew can
 * say "we ran KTJM last night" out loud and a bug report can name the night it
 * happened on. Reversible, so a fixed night can be dialled up again.
 */
const ALPHABET = 'BCDFGHJKLMNPQRSTVWXYZ23456789';
/** How many nights four letters can address. 29^4. */
const NAMEABLE = ALPHABET.length ** 4;

export function seedName(seed: number): string {
  let n = seed >>> 0;
  let out = '';
  for (let i = 0; i < 4; i++) {
    out += ALPHABET[n % ALPHABET.length];
    n = Math.floor(n / ALPHABET.length);
  }
  return out;
}

/**
 * A name back to a seed, or null if it is not one.
 *
 * Exact for every seed `mintSeed` can produce, which is the whole reason that
 * function is bounded the way it is: a player who types the name off the card
 * gets the building they were looking at, not one that merely shares a label.
 */
export function seedFromName(name: string): number | null {
  const up = name.trim().toUpperCase();
  if (up.length !== 4) return null;
  let n = 0;
  for (let i = 3; i >= 0; i--) {
    const k = ALPHABET.indexOf(up[i]);
    if (k < 0) return null;
    n = n * ALPHABET.length + k;
  }
  return n >>> 0;
}
